import { api } from '@/lib/axios';

export interface DashboardStats {
  ca_facture_mois: number;
  marches_en_cours: number;
  chantiers_actifs: number;
  chantiers_total: number;
  chantiers_acheves: number;
  tresorerie_disponible: number;
  depenses_mois: number;
  situations_a_soumettre: number;
  ca_mensuel: Array<{ annee: number; mois: number; ca: number }>;
}

export async function getDashboardStats(): Promise<DashboardStats> {
  const { data } = await api.get<DashboardStats>('/dashboard/stats');
  return data;
}

/* ----------------------- Tableau de bord PATRON --------------------------- */
export interface ChantierMarge { id: string; nom: string; code: string; ca: number; cout: number; marge: number; marge_pct: number | null }
export interface CoherenceReport {
  regle1_ca_mois_egal_bar: boolean;
  regle2_ca_mois_inf_marches: boolean;
  regle3_marches_coherents: boolean;
  anomalies_marche: { marche: string; cumul_facture: number; montant_contractuel: number; depassement: number }[];
}

/** KPI réconciliés (source de vérité unique) — endpoint /dashboard/kpis. */
export interface KpisData {
  ca_facture_mois: number;
  ca_facture_annee: number;
  marches_en_cours: number;
  chantiers_actifs: number;
  tresorerie_disponible: number;
  ca_mensuel: { annee: number; mois: number; label: string; ca: number }[];
  coherence: CoherenceReport;
}

export async function getDashboardKpis(): Promise<KpisData> {
  const { data } = await api.get<KpisData>('/dashboard/kpis');
  return data;
}

export interface PatronData {
  vitales: { tresorerie: number; ca_mois: number; ca_var_pct: number; ca_annee: number; marge_mois: number; marge_pct: number; carnet_commandes: number; marches_en_cours: number; chantiers_actifs: number };
  coherence: CoherenceReport;
  sante: { creances: number; creances_60j: number; dettes: number; resultat_economique_reel: number; resultat_comptable_fiscal: number; ecart_charges_non_deductibles: number; resultat_net: number; depenses_non_comptabilisees: number; non_comptabilisees_pct: number };
  rentabilite: ChantierMarge[];
  top5_ca: { id: string; nom: string; ca: number }[];
  pipeline: { ao_nombre: number; ao_montant: number; taux_transformation: number; remportes: number; perdus: number };
  graphiques: {
    ca_mensuel: { label: string; ca: number }[];
    marge_mensuelle: { label: string; marge: number }[];
    tresorerie_prev: { label: string; solde: number }[];
    repartition_ca: { name: string; value: number }[];
  };
  alertes: {
    depassement_budget: { id: string; nom: string; budget: number; cout: number; depassement_pct: number; lien: string }[];
    cautions: { reference: string; banque: string | null; date: string; jours: number; montant: number; lien: string }[];
    creances_souffrance: { chantier: string; numero: number; montant: number; jours: number | null; lien: string }[];
    g50: { libelle: string; date: string; jours: number; lien: string };
    retard_planning: { id: string; nom: string; jours_retard: number; lien: string }[];
  };
}

export async function getDashboardPatron(): Promise<PatronData> {
  const { data } = await api.get<PatronData>('/dashboard/patron');
  return data;
}

/* -------------------- Tableau de bord OPÉRATIONNEL ------------------------ */
export interface AvancementChantier { id: string; nom: string; code: string; avancement: number; date_fin: string | null; jours_restants: number | null; statut: string; retard: boolean }
export interface TachePlanning { tache: string; chantier: string; date: string; jours: number; avancement: number; lien: string }
export interface OperationnelData {
  kpis: { chantiers_actifs: number; effectif_present: number; engins_service: number; engins_total: number; taches_retard: number };
  avancement: AvancementChantier[];
  planning: TachePlanning[];
  echeances_jour: TachePlanning[];
  a_traiter: { bm_attente: number; livraisons_attendues: number; situations_a_etablir: number; maintenances_planifiees: number };
  pointage_jour: { total_attendus: number; total_pointes: number; par_chantier: { chantier: string; attendus: number; pointes: number; manquants: number }[] };
  alertes: {
    engins_maintenance: { id: string; code: string; designation: string; raison: string; date: string | null; lien: string }[];
    retard_planning: { id: string; nom: string; jours_retard: number; lien: string }[];
    stocks_bas: { designation: string; quantite: number; seuil: number; unite: string; lien: string }[];
  };
}

export async function getDashboardOperationnel(): Promise<OperationnelData> {
  const { data } = await api.get<OperationnelData>('/dashboard/operationnel');
  return data;
}
